import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import type {
  DiscoveryEvent,
  ProfileSummary,
  VenuePublicDeal,
  VenueSummary,
} from '@/lib/api/apiTypes';
import { APP_FEATURES } from '@/lib/app/features';
import { useFeedPreferences } from '@/features/events/feed/feedPreferences';
import { formatEventDayLabel, formatEventTime } from '@/features/events/eventFormatting';
import { getEventHref, getPublicIdentityHref, getVenueHref } from '@/lib/navigation/routes';
import { cn } from '@/lib/utils';
import { Image } from 'expo-image';
import { router } from 'expo-router';
import {
  Building2Icon,
  CalendarDaysIcon,
  SearchIcon,
  TicketPercentIcon,
  UserRoundIcon,
  XIcon,
  type LucideIcon,
} from 'lucide-react-native';
import * as React from 'react';
import { Keyboard, Pressable, View } from 'react-native';
import {
  buildRecentDeal,
  buildRecentEvent,
  buildRecentProfile,
  buildRecentQuery,
  buildRecentVenue,
} from './recentSearchEntries';
import { useRecentSearches } from './useRecentSearches';
import { useSearch } from './useSearch';

const QUICK_SEARCH_SUGGESTIONS = [
  'Tonight',
  'Live music',
  'Happy hour',
  'Free',
  'Near me',
] as const;

type CompactSearchResult =
  | {
      key: string;
      type: 'event';
      title: string;
      subtitle: string;
      meta: string;
      item: DiscoveryEvent;
    }
  | {
      key: string;
      type: 'venue';
      title: string;
      subtitle: string;
      meta: string;
      item: VenueSummary;
    }
  | {
      key: string;
      type: 'deal';
      title: string;
      subtitle: string;
      meta: string;
      item: VenuePublicDeal;
    }
  | {
      key: string;
      type: 'profile';
      title: string;
      subtitle: string;
      meta: string;
      item: ProfileSummary;
    };

function getResultIcon(type: CompactSearchResult['type']): LucideIcon {
  switch (type) {
    case 'event':
      return CalendarDaysIcon;
    case 'venue':
      return Building2Icon;
    case 'deal':
      return TicketPercentIcon;
    case 'profile':
      return UserRoundIcon;
  }
}

function getResultImageURL(result: CompactSearchResult) {
  switch (result.type) {
    case 'venue':
      return result.item.logo_url?.trim() || null;
    case 'deal':
      return result.item.logo_url?.trim() || null;
    case 'profile':
      return result.item.avatar_url?.trim() || null;
    case 'event':
      return null;
  }
}

function getResultImageClassName(result: CompactSearchResult) {
  if (result.type === 'venue' || result.type === 'profile') {
    return 'rounded-full';
  }
  return 'rounded-xl';
}

function getCompactResults({
  deals,
  events,
  profiles,
  venues,
}: {
  deals: VenuePublicDeal[];
  events: DiscoveryEvent[];
  profiles: ProfileSummary[];
  venues: VenueSummary[];
}) {
  const compactEvents: CompactSearchResult[] = events.slice(0, 4).map((event) => ({
    key: `event:${event.id}`,
    type: 'event',
    title: event.title,
    subtitle: event.venue_name || 'Venue TBD',
    meta: `${formatEventDayLabel(event.starts_at)} · ${formatEventTime(event.starts_at)}`,
    item: event,
  }));

  const compactVenues: CompactSearchResult[] = venues.slice(0, 3).map((venue) => ({
    key: `venue:${venue.id}`,
    type: 'venue',
    title: venue.name,
    subtitle: venue.address || 'Venue',
    meta: venue.is_verified ? 'Verified venue' : 'Venue',
    item: venue,
  }));

  const compactDeals: CompactSearchResult[] = deals.slice(0, 2).map((deal) => ({
    key: `deal:${deal.id}`,
    type: 'deal',
    title: deal.title,
    subtitle: deal.venue_name,
    meta: 'Deal',
    item: deal,
  }));

  const compactProfiles: CompactSearchResult[] = profiles
    .filter((profile) => !profile.is_venue)
    .slice(0, 2)
    .map((profile) => ({
      key: `profile:${profile.user_id}`,
      type: 'profile',
      title: profile.display_name || profile.username,
      subtitle: `@${profile.username}`,
      meta: profile.is_limited ? 'Friends only' : 'Profile',
      item: profile,
    }));

  return [...compactEvents, ...compactVenues, ...compactDeals, ...compactProfiles].slice(0, 8);
}

export function WebSearchCombobox({
  autoFocus = false,
  className,
  variant = 'hero',
}: {
  autoFocus?: boolean;
  className?: string;
  variant?: 'hero' | 'nav';
}) {
  const [query, setQuery] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const setFeedTab = useFeedPreferences((state) => state.setFeedTab);
  const setTimeWindow = useFeedPreferences((state) => state.setTimeWindow);
  const blurTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const trimmedQuery = query.trim();
  const searchEnabled = APP_FEATURES.search && isOpen && trimmedQuery.length >= 2;
  const searchQuery = useSearch(trimmedQuery, searchEnabled, 8);
  const { recentSearches, saveSearch } = useRecentSearches();
  const compactResults = React.useMemo(
    () =>
      getCompactResults({
        deals: searchQuery.data?.deals ?? [],
        events: searchQuery.data?.events ?? [],
        profiles: searchQuery.data?.profiles ?? [],
        venues: searchQuery.data?.venues ?? [],
      }),
    [
      searchQuery.data?.deals,
      searchQuery.data?.events,
      searchQuery.data?.profiles,
      searchQuery.data?.venues,
    ]
  );
  const recentQueryEntries = React.useMemo(
    () => recentSearches.filter((entry) => entry.query).slice(0, 4),
    [recentSearches]
  );
  const showSuggestions = trimmedQuery.length < 2;

  React.useEffect(
    () => () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    },
    []
  );

  const cancelPendingBlur = React.useCallback(() => {
    if (!blurTimeoutRef.current) {
      return;
    }

    clearTimeout(blurTimeoutRef.current);
    blurTimeoutRef.current = null;
  }, []);

  const openSuggestions = React.useCallback(() => {
    cancelPendingBlur();
    setIsOpen(true);
  }, [cancelPendingBlur]);

  const scheduleSuggestionsClose = React.useCallback(() => {
    cancelPendingBlur();
    blurTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      blurTimeoutRef.current = null;
    }, 120);
  }, [cancelPendingBlur]);

  const handleClearQuery = React.useCallback(() => {
    setQuery('');
  }, []);

  const openFullSearch = React.useCallback(
    (nextQuery = trimmedQuery) => {
      const nextTrimmedQuery = nextQuery.trim();
      if (nextTrimmedQuery.length >= 2) {
        void saveSearch(buildRecentQuery(nextTrimmedQuery));
        router.push({ pathname: '/search', params: { q: nextTrimmedQuery } });
        return;
      }

      router.push('/search');
    },
    [saveSearch, trimmedQuery]
  );

  const closeSearch = React.useCallback(() => {
    cancelPendingBlur();
    setIsOpen(false);
    Keyboard.dismiss();
  }, [cancelPendingBlur]);

  const openResult = React.useCallback(
    (result: CompactSearchResult) => {
      switch (result.type) {
        case 'event':
          void saveSearch(buildRecentEvent(result.item));
          closeSearch();
          router.navigate(getEventHref(result.item.id, result.item.title));
          return;
        case 'venue':
          void saveSearch(buildRecentVenue(result.item));
          closeSearch();
          router.navigate(
            getVenueHref(
              result.item.id,
              { source: 'web_header_search' },
              { name: result.item.name }
            )
          );
          return;
        case 'deal':
          void saveSearch(buildRecentDeal(result.item));
          closeSearch();
          router.navigate(
            getVenueHref(
              result.item.venue_id,
              { source: 'web_header_search' },
              { name: result.item.venue_name }
            )
          );
          return;
        case 'profile':
          void saveSearch(buildRecentProfile(result.item));
          closeSearch();
          router.navigate(getPublicIdentityHref(result.item, { source: 'web_header_search' }));
          return;
      }
    },
    [closeSearch, saveSearch]
  );

  const openSuggestion = React.useCallback(
    (suggestion: string) => {
      if (suggestion === 'Near me') {
        setFeedTab('nearby');
        setTimeWindow('today');
        closeSearch();
        router.navigate('/events');
        return;
      }

      setQuery(suggestion);
      openFullSearch(suggestion);
    },
    [closeSearch, openFullSearch, setFeedTab, setTimeWindow]
  );

  return (
    <View className={cn('relative z-50 w-full', variant === 'hero' && 'max-w-2xl', className)}>
      <View
        className={cn(
          'border',
          variant === 'nav'
            ? 'rounded-none border-0 border-b bg-transparent px-1 py-1'
            : 'rounded-2xl bg-surface-alt/90 px-3 py-2.5',
          isOpen
            ? variant === 'nav'
              ? 'border-white/40'
              : 'border-primary-edge/60'
            : variant === 'nav'
              ? 'border-white/20'
              : 'border-border'
        )}>
        <View className="flex-row items-center gap-3">
          <Icon
            as={SearchIcon}
            size={15}
            className={variant === 'nav' ? 'text-white/40' : 'text-muted-foreground'}
          />
          <Input
            autoCapitalize="none"
            autoCorrect={false}
            autoFocus={autoFocus}
            className={cn(
              'flex-1 border-0 bg-transparent px-0 shadow-none focus-visible:border-transparent focus-visible:ring-0 dark:bg-transparent',
              variant === 'nav'
                ? 'h-8 text-sm text-white placeholder:text-white/50'
                : 'h-10 text-base'
            )}
            style={{ backgroundColor: 'transparent' }}
            onBlur={scheduleSuggestionsClose}
            onChangeText={setQuery}
            onFocus={openSuggestions}
            onKeyPress={(event) => {
              if (event.nativeEvent.key === 'Escape') {
                setIsOpen(false);
              }
            }}
            onSubmitEditing={() => openFullSearch()}
            placeholder={
              variant === 'nav' ? 'Search Radious' : 'Search events, deals, venues, or profiles'
            }
            returnKeyType="search"
            value={query}
          />
          {query ? (
            <Pressable
              accessibilityLabel="Clear search"
              className="size-8 items-center justify-center rounded-full active:opacity-80"
              onPress={handleClearQuery}>
              <Icon as={XIcon} size={15} className="text-muted-foreground" />
            </Pressable>
          ) : null}
        </View>
      </View>

      {isOpen ? (
        <View
          className={cn(
            'absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden border border-border bg-card shadow-xl shadow-black/20',
            variant === 'nav' ? 'right-auto w-[360px] rounded-2xl' : 'rounded-3xl'
          )}>
          {showSuggestions ? (
            <View className="py-1">
              {recentQueryEntries.length > 0 ? (
                <>
                  <View className="px-4 pb-1 pt-2.5">
                    <Text variant="small" className="font-semibold text-muted-foreground">
                      Recent
                    </Text>
                  </View>
                  {recentQueryEntries.map((entry) => (
                    <Pressable
                      key={entry.key}
                      className="flex-row items-center gap-3 px-4 py-2.5 hover:bg-surface-alt active:opacity-70"
                      onPress={() => {
                        if (entry.query) {
                          setQuery(entry.query);
                          openFullSearch(entry.query);
                        }
                      }}>
                      <Icon as={SearchIcon} size={14} className="text-muted-foreground" />
                      <Text variant="small" className="font-medium text-foreground">
                        {entry.title}
                      </Text>
                    </Pressable>
                  ))}
                  <View className="mx-4 my-1 border-t border-border" />
                </>
              ) : null}
              <View className="px-4 pb-1 pt-2">
                <Text variant="small" className="font-semibold text-muted-foreground">
                  Suggested
                </Text>
              </View>
              {QUICK_SEARCH_SUGGESTIONS.map((suggestion) => (
                <Pressable
                  key={suggestion}
                  className="flex-row items-center gap-3 px-4 py-2.5 hover:bg-surface-alt active:opacity-70"
                  onPress={() => openSuggestion(suggestion)}>
                  <Icon as={SearchIcon} size={14} className="text-primary/60" />
                  <Text variant="small" className="font-medium text-foreground">
                    {suggestion}
                  </Text>
                </Pressable>
              ))}
              <View className="mx-4 my-1 border-t border-border" />
              <Pressable
                className="flex-row items-center justify-between px-4 py-3 active:opacity-70"
                onPress={() => openFullSearch()}>
                <Text variant="small" className="font-semibold text-foreground">
                  Open full search
                </Text>
                <Text variant="small" className="font-semibold text-primary">
                  /search →
                </Text>
              </Pressable>
            </View>
          ) : searchQuery.isPending ? (
            <View className="p-4">
              <Text variant="small" className="font-medium text-muted-foreground">
                Searching Radious…
              </Text>
            </View>
          ) : compactResults.length > 0 ? (
            <View>
              {compactResults.map((result) => {
                const ResultIcon = getResultIcon(result.type);
                const imageURL = getResultImageURL(result);
                const imageClassName = getResultImageClassName(result);
                return (
                  <Pressable
                    key={result.key}
                    className="min-h-[76px] flex-row items-center gap-3 border-b border-border px-4 py-3.5 hover:bg-surface-alt/60 active:opacity-80"
                    onPress={() => openResult(result)}>
                    <View className={cn('size-11 overflow-hidden bg-primary-soft', imageClassName)}>
                      {imageURL ? (
                        <Image
                          source={{ uri: imageURL }}
                          className="size-full"
                          contentFit="cover"
                          cachePolicy="memory-disk"
                        />
                      ) : (
                        <View className="size-full items-center justify-center">
                          <Icon as={ResultIcon} size={17} className="text-primary" />
                        </View>
                      )}
                    </View>
                    <View className="min-w-0 flex-1 gap-0.5">
                      <View className="flex-row items-start gap-3">
                        <Text
                          numberOfLines={2}
                          className="min-w-0 flex-1 font-semibold leading-5 text-foreground">
                          {result.title}
                        </Text>
                        <Text
                          variant="small"
                          numberOfLines={1}
                          className="max-w-[112px] shrink-0 pt-0.5 text-right font-semibold text-muted-foreground">
                          {result.meta}
                        </Text>
                      </View>
                      <Text numberOfLines={1} variant="small" className="text-muted-foreground">
                        {result.subtitle}
                      </Text>
                    </View>
                  </Pressable>
                );
              })}
              <Pressable
                className="flex-row items-center justify-between bg-surface-alt px-4 py-3 active:opacity-80"
                onPress={() => openFullSearch()}>
                <Text className="font-semibold text-foreground">View all results</Text>
                <Text variant="small" className="font-semibold text-primary">
                  /search
                </Text>
              </Pressable>
            </View>
          ) : (
            <View className="gap-3 p-4">
              <Text className="font-semibold text-foreground">No quick matches yet.</Text>
              <Text variant="small" className="text-muted-foreground">
                Try a venue, event title, username, or a broader phrase.
              </Text>
              <Pressable
                className="self-start rounded-full bg-primary px-3.5 py-2 active:opacity-80"
                onPress={() => openFullSearch()}>
                <Text variant="small" className="font-bold text-primary-foreground">
                  Search deeper
                </Text>
              </Pressable>
            </View>
          )}
        </View>
      ) : null}
    </View>
  );
}
